import React, { FC, useCallback, useMemo, useState } from 'react'
import userManual from './metadata.json' 
import ApplicationTitleAppendix from 'components/ApplicationTitleAppendix'
import { Button, Input, InputGroup, InputGroupAddon, ListGroup, ListGroupItem } from 'reactstrap'
import { useHistory, useLocation } from 'react-router'
import { RiArrowGoBackFill } from 'react-icons/ri'
import { useApi, useApiLoader } from 'utils/API'
import { FaLink, FaPlus, FaTimes, FaTrash } from 'react-icons/fa'
import ProcessingButton from 'components/ProcessingButton'
import { confirm, filePrompt } from 'utils/Prompts'
import Translation from 'utils/Language/Translation'
import { getServerUrl } from 'component_utils/browserSafeUtils'
import RoleProtected from 'components/RoleProtected'
import SimpleModal, { ModalRef } from 'components/SimpleModal'
import { sleep } from 'component_utils/utils'
import { IApp, useFlatMenu } from 'statics/route_data'
import { useTranslations } from 'utils/language'
import { fromPairs } from 'lodash'

const Inner: FC<{ selected: string, setSelected: any, defaultAppFilter?: any }> = ({ selected, setSelected, defaultAppFilter }) => {
    const api = useApi();
    const [search, setSearch] = useState("")
    const { data: userArticles, reload: reloadUserArticles } = useApiLoader(
        useCallback(api => api.fileManagement.getContents({ path: ['user_manuals'] }, false), []),
        [],
        true
    )
    const allManuals = useMemo<{ name: string, path: string, apps: string[], keywords: string[], hasVideo: boolean, userManagedFileName?: string}[]>(() => {
        return [
            ...userManual,
            ...userArticles.map(ua => {
                return {
                    name: ua.name.replace(".html", ""),
                    apps: [],
                    keywords: [],
                    hasVideo: false,
                    path: getServerUrl() + '/userdata/static/user_manuals/' + ua.name,
                    userManagedFileName: ua.name,
                }
            })
        ]
    }, [userArticles])

    const T = useTranslations()
    const allApps = useFlatMenu()
    const appSelects = useMemo(() => allApps.map(it => ({ value: it.id, label: T(it.title) })), [T, allApps])
    const [selectedApp, setSelectedApp] = useState(defaultAppFilter || '')

    const articles = useMemo(() => {
      return allManuals
        .filter(it => !selectedApp || it.apps.includes(selectedApp))
        .filter(it => it.name.toLowerCase().includes(search.toLowerCase()))
    }, [search, selectedApp, allManuals])

    return (
        <div className='w-100 h-100'>
            {!selected && (
                <div className='flex-container'>
                    <InputGroup
                        className='mb-2'
                    >
                        <Input
                            placeholder='Search'
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <InputGroupAddon addonType="append">
                            <RoleProtected roles={['usermanualAuthCreateArticles']}>
                                <ProcessingButton 
                                    color="success" 
                                    onClick={async () => {
                                        const result = await filePrompt(<Translation name="T.misc.newManualFile"/>)
                                        if (result) {
                                            const { name, contents } = result
                                            await api.fileManagement.uploadFile({
                                                path: ['user_manuals', name],
                                                contents: contents
                                            })
                                            reloadUserArticles()
                                        }
                                    }}
                                >
                                    <FaPlus />
                                </ProcessingButton>
                            </RoleProtected>
                        </InputGroupAddon>
                    </InputGroup>
                    <InputGroup
                      className='mb-2'
                    >
                      <Input
                        type='select' 
                        value={selectedApp} 
                        onChange={e => setSelectedApp(e.target.value)}
                      >
                        <option value={''} key={''}>Application filter</option>
                        {appSelects.map(it => (
                          <option value={it.value} key={it.value}>{it.label}</option>
                        ))}
                      </Input>
                      <InputGroupAddon addonType="append">
                        <ProcessingButton 
                          disabled={!selectedApp}
                          onClick={async () => {
                            setSelectedApp('')
                          }}
                        >
                          <FaTimes />
                        </ProcessingButton>
                      </InputGroupAddon>
                    </InputGroup>
                    <div className='nested-flex-container'>
                        <ListGroup>
                            {articles.map((it, idx) => {
                                return (
                                    <ListGroupItem
                                        key={idx} 
                                        onClick={() => setSelected(it.path)}
                                        className='d-flex w-100'
                                    >
                                        <div className="flex-fill d-flex align-items-center px-2 d-none d-md-flex">
                                            {it.name}
                                        </div>
                                        {it.userManagedFileName && (
                                            <RoleProtected roles={['usermanualAuthCreateArticles']}>
                                                <div className="d-flex align-items-center px-2">
                                                    <ProcessingButton className='py-0' color='danger' onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (await confirm(<Translation name="T.warnings.areYouSureYouWantToDelete"/>)) {
                                                            await api.fileManagement.deleteObject({
                                                                path: ['user_manuals', it.userManagedFileName]
                                                            })
                                                            reloadUserArticles()
                                                        }
                                                    }}>
                                                        <FaTrash/>
                                                    </ProcessingButton>
                                                </div>
                                            </RoleProtected>
                                        )}
                                    </ListGroupItem>
                                )
                            })}

                            {articles.length === 0 && (
                              <ListGroupItem
                                className='text-center w-100'
                              >
                                <Translation name="T.misc.noManualsFound"/>
                              </ListGroupItem>
                            )}
                        </ListGroup>
                    </div>
                </div>
            )}
            {selected && (
                <iframe
                    src={selected}
                    title="Guide"
                    frameBorder="0"
                    style={{ border: '0px' }}
                    className="w-100 h-100"
                />
            )}
        </div>
    )
}

const findAppId = (currentPath: string, availableApps: IApp[]) => {
  const appIds = fromPairs(availableApps.map(it => [it.path, it.id]));

  if (appIds[currentPath]) {
    return appIds[currentPath];
  }

  for (const key of Object.keys(appIds)) {
    if (currentPath.startsWith(key)) {
      return appIds[currentPath];
    }
  }

  return null;
}

export const UserManualAppEmbed = React.forwardRef<ModalRef, {}>((_, ref) => {
  const [selected, setSelected] = useState()
  const location = useLocation();
  const availableApps = useFlatMenu();
  const pathname = location.pathname
  const appId = useMemo(() => {
    return findAppId(pathname, availableApps)
  }, [pathname, availableApps])

  return (
    <SimpleModal 
      ref={ref} 
      fullscreen
      id='user-manual-modal'
      title={
        <>
          <Translation name="T.menu.userManual" />
          {' '}
          {selected && <Button className='py-0'><RiArrowGoBackFill onClick={() => setSelected(null)}/></Button>}
          {selected && <Button className='py-0'><FaLink onClick={async () => {
            await navigator.clipboard.writeText(
              window.location.origin + "/about/user_manual?article="+encodeURIComponent(selected)
            )
            await sleep(250)
          }}/></Button>}
        </>
      } 
    >
      <div className="flex-container">
        <Inner
          selected={selected}
          setSelected={setSelected}
          defaultAppFilter={appId}
        />
      </div>
    </SimpleModal>
  );
});


export default () => {  
  const { search: searchParams } = useLocation();
  const query = new URLSearchParams(searchParams);
  const history = useHistory()
  const selected = query.get("article")
  const setSelected = (path: string) => {
    history.push("/about/user_manual?article="+encodeURIComponent(path))
  }

  return (
    <>
      <ApplicationTitleAppendix>
        {selected && <>(<RiArrowGoBackFill onClick={() => history.goBack()}/>)</>}
      </ApplicationTitleAppendix>
      <Inner
        selected={selected}
        setSelected={setSelected}
      />
    </>
  ) 
}